import { Injectable, Type } from '@angular/core';
import { BaseService } from '@services/base-service.service';
import { API } from '@config/constants/api.constant';
import { BehaviorSubject, shareReplay } from 'rxjs';
import { IBrandExclusiveList, PayloadPostProductExclusive } from '../products/Products.interface';
import { InputSelectInterface } from '@shared/components/form/input-select/input-select.interface';
import { map } from 'rxjs/operators';
import { Sort } from '@angular/material/sort';
import { IQueryParams } from '@shared/interface/urlparam.interface';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { IListboxBase } from '@shared/components/form/input-listbox-region/input-listbox-region.interface';
import { IDataResResetPasswordDistributor, IDistributorArea, IDistributorType, IDocumentDistributor, IPrintKUL } from './interfaces/distributor.interface';
import { IDetailInformasi, IDetailInformasi_PlafonKredit, IDistributorInformation } from './interfaces/detail-informasi.interface';
import { IDistributorImpact, IHeaderDistributorDetail } from './interfaces/detail-header.interface';
import { ITabList } from '@shared/interface/tablist.interface';
import { TabLabelEnumDetailDistributor, TabLabelEnumDetailDistributor__LabelKey } from './enum/tablist-label.enum';
import { DistributorStatusEnum } from '@config/enum/distributor.enum';
import { TabInformasiDistributorComponent } from './distributor-detail/components/tab-informasi-distributor/tab-informasi-distributor.component';
import { TabPlafonKreditComponent } from './distributor-detail/components/tab-plafon-kredit/tab-plafon-kredit.component';
import { TabProductExclusiveComponent } from './distributor-detail/components/tab-product-exclusive/tab-product-exclusive.component';
import { TabAlamatPengirimanComponent } from './distributor-detail/components/tab-alamat-pengiriman/tab-alamat-pengiriman.component';
import { TabDokumenComponent } from './distributor-detail/components/tab-dokumen/tab-dokumen.component';
import { TabProgramTargetComponent } from './distributor-detail/components/tab-program-target/tab-program-target.component';
import {
  IDetailVerification__DistributorInformation,
  IDetailVerificationNPWP,
  IDetailVerificationPlafonKredit,
  IVerificationDocumentKUL,
} from './interfaces/detail-verification.interface';
import { VerificationTabInformasiDistributorComponent } from './distributor-detail/detail-pending/detail-verification/verification-tab-informasi-distributor/verification-tab-informasi-distributor.component';
import { VerificationTabInformasiKULComponent } from './distributor-detail/detail-pending/detail-verification/verification-tab-informasi-kul/verification-tab-informasi-kul.component';
import { IDetailDocumentVerification } from './interfaces/detail-document-verification.interface';
import { IPayloadRegistrationDeliveryAddress } from './interfaces/detail-registration.interface';
import { IGenericLabelValue } from '@shared/interface/generic';
import { EnumViewCardVerificationSection } from './distributor-detail/detail-pending/detail-registration/distributor-data/distributor-data.data';
import { IDetailPlafonKreditV1 } from './interfaces/detail-plafon-kredit.interface';
import { TabLogComponent } from './distributor-detail/components/tab-log/tab-log.component';
import { IDetailDeliveryV1 } from './interfaces/detail-delivery.interface';
import { IResponseNonActiveCustomer } from '@shared/components/modal/modal.interface';

@Injectable({
  providedIn: 'root',
})
export class DistributorService {
  private distributorInfoSubject = new BehaviorSubject({} as IDetailInformasi);
  distributorDetailHeaderSubject = new BehaviorSubject({} as IHeaderDistributorDetail);
  distributorImpactSubject = new BehaviorSubject({} as IDistributorImpact);

  set DistributorInfoData(data: IDetailInformasi) {
    this.distributorInfoSubject.next(data);
  }

  get DistributorInfoData() {
    return this.distributorInfoSubject.value;
  }

  get DistributorDetailHeader() {
    return this.distributorDetailHeaderSubject.value;
  }

  set DistributorDetailHeader(data: IHeaderDistributorDetail) {
    this.distributorDetailHeaderSubject.next(data);
  }

  get DistributorImpact() {
    return this.distributorImpactSubject.value;
  }

  set DistributorImpact(data: IDistributorImpact) {
    this.distributorImpactSubject.next(data);
  }

  get BasePathDistributorDetail() {
    return '/distributor/detail';
  }

  constructor(
    protected baseService: BaseService,
    protected router: Router // private registrationService: DetailRegistrationDistributorService,
  ) {}

  getDistributorDetailInfo(id: string) {
    return this.baseService.getData<IDetailInformasi>(API.DETAIL_INFORMASI_DISTRIBUTOR + id).pipe(
      shareReplay(),
      map((res) => {
        if (res && res.success) this.DistributorInfoData = res.data;
        return res;
      })
    );
  }

  getDocumentImages(distributorID: string) {
    return this.baseService.getData<IDocumentDistributor>(API.DISTRIBUTOR.GET_DOCUMENT + distributorID).pipe(
      shareReplay(),
      map((resp) => resp && resp.data)
    );
  }

  getListProductExclusive(id: string) {
    return this.baseService.getData<IBrandExclusiveList[]>(API.LIST_BRAND_PRODUCT_EXCLUSIVE + id);
  }

  createProductExclusive(data: PayloadPostProductExclusive) {
    return this.baseService.postData<PayloadPostProductExclusive>(API.CREATE_PRODUCT_EXCLUSIVE, data).pipe(shareReplay());
  }

  updateProductExclusive(data: PayloadPostProductExclusive) {
    return this.baseService.postData<PayloadPostProductExclusive>(API.UPDATE_PRODUCT_EXCLUSIVE, data).pipe(shareReplay());
  }

  getListDistributorType() {
    return this.baseService.getData<IDistributorType[]>(API.LIST_TYPE).pipe(
      map((resp) => {
        if (!resp) {
          return [];
        }

        return resp.data.map((res) => {
          const inputSelector: InputSelectInterface = new InputSelectInterface();
          const { enum_value, enum_string } = res;
          inputSelector.value = enum_value;
          inputSelector.display = enum_string;
          return inputSelector;
        });
      })
    );
  }

  getListDistributorArea() {
    return this.baseService.getData<IDistributorArea[]>(API.LIST_DISTRIBUTOR_AREA).pipe(map((resp) => resp));
  }

  sortDataSource(sortParams: Sort, activeRouteSnapshot: ActivatedRouteSnapshot) {
    if (sortParams.direction) {
      const dataParam: IQueryParams = {
        sort: sortParams.active + ',' + sortParams.direction,
      };
      this.router
        .navigate([], {
          queryParams: {
            ...dataParam,
          },
          queryParamsHandling: 'merge',
        })
        .then(() => null);
    } else {
      const dataParam = { ...activeRouteSnapshot.queryParams };
      delete dataParam.sort;
      this.router.navigate([], { queryParams: dataParam }).then(() => null);
    }
  }

  getDataPrintKUL(distributorID: string) {
    return this.baseService.getData<IPrintKUL>(API.DETAIL_KUL + distributorID).pipe(
      shareReplay(),
      map((resp) => resp && resp.data)
    );
  }

  getDistributorSubArea(distributorID: string) {
    return this.baseService.getData<IListboxBase[]>(API.LIST_DISTRIBUTOR_SUB_AREA + distributorID).pipe(
      shareReplay(),
      map((resp) => {
        return resp;
      })
    );
  }

  getDistributorDetailHeader(distributorID: string) {
    // implement integrate api detail header
    return this.baseService.getData<IHeaderDistributorDetail>(API.DISTRIBUTOR.GET_DETAIL_HEADER + distributorID).pipe(
      shareReplay(),
      map((resp) => {
        if (resp && resp.data) {
          this.DistributorDetailHeader = resp.data;
          return resp && resp.data;
        }
      })
    );
  }

  getDistributorImpacted(distributorID: string) {
    return this.baseService.getData<IDistributorImpact>(API.DISTRIBUTOR.GET_IMPACT_DISTRIBUTOR + distributorID).pipe(
      shareReplay(),
      map((resp) => {
        if (resp && resp.data) {
          this.DistributorImpact = resp.data;
          return resp && resp.data;
        }
      })
    );
  }

  generateDistributorDetailTabList(status?: string | DistributorStatusEnum) {
    const tabList = <ITabList[]>[];
    let _i = 0;
    let _k: keyof typeof TabLabelEnumDetailDistributor;
    for (_k in TabLabelEnumDetailDistributor) {
      tabList.push({
        enum: _k,
        tab: TabLabelEnumDetailDistributor__LabelKey[_k],
        labelKey: TabLabelEnumDetailDistributor__LabelKey[_k],
        privilege: null,
        index: _i,
      });
      _i++;
    }
    if (status) return this.filterTabListByStatus(status, tabList);
    return tabList;
  }

  filterTabListByStatus(status: string | DistributorStatusEnum, tabList: ITabList[]): ITabList[] {
    const TAB = TabLabelEnumDetailDistributor;

    const removeTabs = (...tabs: string[]) => tabList.filter((item) => !tabs.includes(item.enum));

    const keepOnlyTabs = (...tabs: string[]) => tabList.filter((item) => tabs.includes(item.enum));

    switch (status) {
      case DistributorStatusEnum.REGISTRATION_REJECTED:
      case DistributorStatusEnum.REGISTRATION_REJECTED_FINANCE:
      case DistributorStatusEnum.VERIFICATION_REJECTED:
        return removeTabs(TAB.TAB_NPWP_INFORMATION, TAB.TAB_KUL_INFORMATION, TAB.TAB_LOG, TAB.TAB_PRODUCT_EXCLUSIVE);

      case DistributorStatusEnum.PENDING_REGISTRATION:
      case DistributorStatusEnum.PENDING_REGISTRATION_FINANCE:
        return keepOnlyTabs(TAB.TAB_NPWP_INFORMATION, TAB.TAB_DISTRIBUTOR_INFORMATION);

      case DistributorStatusEnum.PENDING_EDIT_VERIFICATION:
      case DistributorStatusEnum.PENDING_EDIT_VERIFICATION_FINANCE:
        return keepOnlyTabs(TAB.TAB_PLAFON_CREDIT, TAB.TAB_DISTRIBUTOR_INFORMATION).reverse();

      case DistributorStatusEnum.PENDING_VERIFICATION:
        return keepOnlyTabs(TAB.TAB_KUL_INFORMATION, TAB.TAB_DISTRIBUTOR_INFORMATION);

      case DistributorStatusEnum.ACTIVE:
      case DistributorStatusEnum.NON_ACTIVATED:
        return removeTabs(TAB.TAB_NPWP_INFORMATION, TAB.TAB_KUL_INFORMATION);

      case DistributorStatusEnum.REGISTERED:
        return removeTabs(TAB.TAB_NPWP_INFORMATION, TAB.TAB_KUL_INFORMATION, TAB.TAB_LOG, TAB.TAB_PRODUCT_EXCLUSIVE);

      default:
        return tabList;
    }
  }

  setTabListComponent(tabEnum: string | TabLabelEnumDetailDistributor, statusEnum?: string) {
    let _component!: Type<any>;

    if (statusEnum === DistributorStatusEnum.PENDING_VERIFICATION) {
      switch (tabEnum) {
        case TabLabelEnumDetailDistributor.TAB_DISTRIBUTOR_INFORMATION:
          _component = VerificationTabInformasiDistributorComponent;
          break;
        case TabLabelEnumDetailDistributor.TAB_KUL_INFORMATION:
          _component = VerificationTabInformasiKULComponent;
          break;
      }

      return _component;
    }

    // for general tab render
    switch (tabEnum) {
      case TabLabelEnumDetailDistributor.TAB_DISTRIBUTOR_INFORMATION:
        _component = TabInformasiDistributorComponent;
        break;
      case TabLabelEnumDetailDistributor.TAB_PLAFON_CREDIT:
        _component = TabPlafonKreditComponent;
        break;
      case TabLabelEnumDetailDistributor.TAB_PRODUCT_EXCLUSIVE:
        _component = TabProductExclusiveComponent;
        break;
      case TabLabelEnumDetailDistributor.TAB_PROGRAM_TARGET:
        _component = TabProgramTargetComponent;
        break;
      case TabLabelEnumDetailDistributor.TAB_ALAMAT_PENGIRIMAN:
        _component = TabAlamatPengirimanComponent;
        break;
      case TabLabelEnumDetailDistributor.TAB_DOCUMENTS:
        _component = TabDokumenComponent;
        break;
      case TabLabelEnumDetailDistributor.TAB_LOG:
        _component = TabLogComponent;
        break;
    }

    return _component;
  }

  capitalizeWords(input: string): string {
    if (!input) return input; // Handle empty or undefined strings
    return input
      .split(' ') // Split the string into words by spaces
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(' '); // Join the words back into a single string
  }

  generateNoteRevision(revisionNoteData: IPayloadRegistrationDeliveryAddress) {
    if (Object.keys(revisionNoteData).length <= 0) return;
    const data: IGenericLabelValue[] = [];
    Object.keys(revisionNoteData.revision_note).forEach((key) => {
      const _label = EnumViewCardVerificationSection[key.toUpperCase() as keyof typeof EnumViewCardVerificationSection];
      const _value = revisionNoteData.revision_note[key as keyof typeof revisionNoteData.revision_note];
      if (!_value) return;
      if (_label === EnumViewCardVerificationSection.DELIVERY_ADDRESS) {
      } else {
        data.push({ label: this.capitalizeWords(_label), value: _value });
      }
    });
    revisionNoteData.delivery_address?.forEach((val, index) => {
      if (!val.note) return;
      data.push({ label: 'Alamat Pengiriman ke-' + (index + 1), value: val.note });
    });
    return data;
  }

  getDistributorInformation(id: string, status?: string | DistributorStatusEnum) {
    let _apiUrl: string = '';
    if (status) {
      if (status === DistributorStatusEnum.PENDING_NONACTIVATED) {
        _apiUrl = API.DISTRIBUTOR.GET_DETAIL_PENDING_NON_ACTIVATED;
      } else {
        _apiUrl =
          status && status === DistributorStatusEnum.PENDING_REGISTRATION_FINANCE
            ? API.DISTRIBUTOR.GET_DETAIL_VERIFICATION.INFORMASI_DISTRIBUTOR
            : API.DISTRIBUTOR.GET_DETAIL_DISTRIBUTOR_INFORMATION;
      }
    }
    return this.baseService.getData<IDistributorInformation>(_apiUrl + id).pipe(shareReplay());
  }

  getDetailVerificationInformasiKUL(id: string) {
    return this.baseService.getData<IDetailDocumentVerification<IVerificationDocumentKUL>>(API.DISTRIBUTOR.GET_DETAIL_VERIFICATION.INFORMASI_KUL + id).pipe(shareReplay());
  }

  getDetailVerificationPlafonKredit(id: string) {
    return this.baseService.getData<IDetailVerificationPlafonKredit>(API.DISTRIBUTOR.GET_DETAIL_VERIFICATION.PLAFON_KREDIT + id).pipe(shareReplay());
  }

  getDetailPlafonKredit(id: string) {
    return this.baseService.getData<IDetailPlafonKreditV1>(API.DISTRIBUTOR.GET_DETAIL_CREDIT_CEILING + id).pipe(shareReplay());
  }

  // edit request finance legacy endpoint to get plafon bill detail data
  getDetailPlafonKreditLegacy(id: string) {
    return this.baseService.getData<IDetailInformasi_PlafonKredit>(API.DETAIL_CREDIT_CELLING_EDIT_REQUEST_FINANCE + id).pipe(shareReplay());
  }

  getDetailDeliveryAddress(id: string) {
    return this.baseService.getData<IDetailDeliveryV1[]>(API.DISTRIBUTOR.GET_DETAIL_DELIVERY_ADDRESS + id).pipe(shareReplay());
  }

  getDetailVerificationNPWP(id: string) {
    return this.baseService.getData<IDetailVerificationNPWP>(API.DISTRIBUTOR.GET_DETAIL_VERIFICATION.INFORMASI_NPWP + id).pipe(shareReplay());
  }

  getDetailVerificationDistributorInformation(id: string) {
    return this.baseService.getData<IDetailVerification__DistributorInformation>(API.DISTRIBUTOR.GET_DETAIL_VERIFICATION.INFORMASI_DISTRIBUTOR + id).pipe(shareReplay());
  }

  onResetPassword() {
    return this.baseService.patchData<IDataResResetPasswordDistributor>(API.RESET_PASSWORD_DISTRIBUTOR + this.DistributorDetailHeader.distributor_account_id).pipe(shareReplay());
  }

  verificationNonActive(data: { approval_enum: string; reject_reason: string }) {
    const id = this.DistributorDetailHeader.distributor_id;
    return this.baseService.putData<IResponseNonActiveCustomer>(API.DISTRIBUTOR.POST_VERIFICATION_NONACTIVE + id, data).pipe(shareReplay());
  }
}

//todo: remove unused
